import { Badge, Button, DataTable, type DataTableColumn, useToast } from "@design";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Pencil, Plus, Trash2, Wallet } from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import {
  type BudgetConfigResponse,
  deleteBudgetConfigMutation,
  errorMessage,
  listBudgetConfigsOptions,
  listBudgetConfigsQueryKey,
} from "@/api";
import { EmptyState } from "@/components/build/EmptyState";
import { RowMenu } from "@/components/build/RowMenu";
import { ConfirmDialog } from "@/components/ConfirmDialog";
import { Text } from "@/components/Text";
import { formatRelativeTime, formatUsd } from "@/i18n/format";
import { AdminPage } from "../AdminPage";
import { useAdminCanEdit } from "../useAdminCanEdit";
import { BudgetFormModal } from "./BudgetFormModal";
import { type BudgetScope, useBudgetTargetName } from "./budgetScope";

type Editing = { budget?: BudgetConfigResponse } | null;

function useColumns(): DataTableColumn<BudgetConfigResponse>[] {
  const { t } = useTranslation("adminUsage");
  const targetName = useBudgetTargetName();
  return [
    {
      key: "scope",
      header: t("budgets.columns.scope"),
      mobile: "primary",
      render: (budget) => (
        <span className="flex min-w-0 items-center gap-2">
          <Text as="span" variant="body-sm" className="truncate">
            {targetName(budget)}
          </Text>
          {budget.scope !== "platform" && (
            <Badge size="compact" variant="neutral">
              {t(`budgets.scope.${budget.scope as BudgetScope}`, { defaultValue: budget.scope })}
            </Badge>
          )}
        </span>
      ),
    },
    {
      key: "amount",
      header: t("budgets.columns.amount"),
      align: "right",
      render: (budget) => (
        <Text as="span" variant="body-sm" className="tabular-nums">
          {formatUsd(budget.monthlyBudgetUsd)}
        </Text>
      ),
    },
    {
      key: "updated",
      header: t("budgets.columns.updated"),
      render: (budget) => (
        <Text as="span" variant="body-sm" tone="tertiary">
          {formatRelativeTime(budget.updatedAt)}
        </Text>
      ),
    },
  ];
}

/** /admin/usage/budgets: monthly spend limits (platform default, per team, per user). */
export function BudgetsPage() {
  const { t } = useTranslation("adminUsage");
  const canEdit = useAdminCanEdit();
  const toast = useToast();
  const queryClient = useQueryClient();
  const targetName = useBudgetTargetName();
  const columns = useColumns();
  const budgets = useQuery(listBudgetConfigsOptions());
  const remove = useMutation(deleteBudgetConfigMutation());
  const [editing, setEditing] = useState<Editing>(null);
  const [deleting, setDeleting] = useState<BudgetConfigResponse | null>(null);

  const confirmDelete = () => {
    if (!deleting) return;
    remove.mutate(
      { path: { budget_id: deleting.id } },
      {
        onSuccess: () => {
          toast.success(t("budgets.confirm.deleted"));
          setDeleting(null);
          void queryClient.invalidateQueries({ queryKey: listBudgetConfigsQueryKey() });
        },
        onError: (error) => toast.error(errorMessage(error, t("budgets.confirm.failed"))),
      },
    );
  };

  const rowMenu = (budget: BudgetConfigResponse) => (
    <RowMenu
      name={targetName(budget)}
      items={[
        { label: t("budgets.menu.edit"), icon: <Pencil size={16} />, onSelect: () => setEditing({ budget }) },
        {
          label: t("budgets.menu.delete"),
          icon: <Trash2 size={16} />,
          danger: true,
          onSelect: () => setDeleting(budget),
        },
      ]}
    />
  );
  const addButton = (
    <Button variant="primary" leadingIcon={<Plus size={16} />} onClick={() => setEditing({})}>
      {t("budgets.add")}
    </Button>
  );

  return (
    <AdminPage title={t("budgets.title")} subtitle={t("budgets.subtitle")} actions={addButton}>
      {budgets.isError ? (
        <Text tone="error" role="alert">
          {t("loadFailed")}
        </Text>
      ) : (
        <DataTable
          aria-label={t("budgets.tableLabel")}
          columns={columns}
          rows={budgets.data?.data ?? []}
          rowKey={(budget) => String(budget.id)}
          loading={budgets.isPending}
          empty={<EmptyState icon={<Wallet size={32} />} title={t("budgets.empty")} body={t("budgets.emptyBody")} />}
          renderActions={canEdit ? rowMenu : undefined}
          actionsLabel={t("budgets.columns.actions")}
        />
      )}
      {editing && <BudgetFormModal budget={editing.budget} onClose={() => setEditing(null)} />}
      <ConfirmDialog
        open={deleting !== null}
        onClose={() => setDeleting(null)}
        title={deleting ? t("budgets.confirm.title", { name: targetName(deleting) }) : undefined}
        body={t("budgets.confirm.body")}
        confirmLabel={t("budgets.confirm.action")}
        loading={remove.isPending}
        onConfirm={confirmDelete}
      />
    </AdminPage>
  );
}
